import { lazy, Suspense } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { enUS } from '@gonfalon/messages/en-US';
import { MessagesProvider } from '@gonfalon/messages/MessagesProvider';

import { AccountsWrapper } from './accounts';
import { HighAccountUsageContainer } from './accounts/components/HighAccountUsageContainer';
import { SignIn, type User } from './auth';
import config from './config';
import { initDatadogRUM } from './datadog';
import { initNewRelicRUM } from './newrelic';
import { NotificationRegion } from './notifications/components';
import { PricingCalculatorContainer } from './pricingCalculator';
import { PlanReviewContainer } from './provisioning/containers/PlanReviewContainer';
import {
  AccessDenied,
  AppLoading,
  LoadingError,
  NotFound,
  Page,
  type RequestError,
  ThemeProvider,
  TopNav,
} from './shared';
import { OmnibarProvider } from './shared/components/Omnibar/OmnibarProvider';
import { useAuth } from './shared/hooks/useAuth';
import { useSettings } from './shared/hooks/useSettings';

const TrialInvitations = lazy(() =>
  import('./provisioning/components/TrialInvitations').then(({ TrialInvitations: Component }) => ({
    default: Component,
  })),
);

type RootProps = Readonly<{
  user: User | null;
  isUserReady: boolean;
  onSignIn: () => void;
  onSignOut: () => void;
}>;

export function Root({ user, isUserReady, onSignIn, onSignOut }: RootProps) {
  const { scopes } = useAuth();
  const settings = useSettings();

  const renderLoading = () => <AppLoading />;

  const renderError = (error: RequestError) => (
    <LoadingError title="Error loading settings" description={error.message.toString()} />
  );

  const renderUnauthenticated = () => (
    <SignIn
      onSignIn={onSignIn}
      troubleshootingURL={config.help.troubleshootingURL}
      slackChannelName={config.help.slackChannelName}
      slackURL={config.help.slackURL}
    />
  );

  const renderAuthenticated = () => (
    <BrowserRouter basename={process.env.BASEURL ? `/${process.env.BASEURL}` : undefined}>
      <OmnibarProvider settings={settings}>
        <Page>
          <Helmet defaultTitle="Vitalstatistix" titleTemplate="%s | Vitalstatistix" />
          {/* @ts-expect-error -- menuItems entries mix optional booleans (scopes.*) into MenuItem['allowed'], which the prop type does not accept as-is */}
          <TopNav
            avatar={user?.email}
            onSignOut={onSignOut}
            searchComponent={scopes.accountsList ? 'omnibar' : null}
            menuItems={[
              { to: '/', name: 'Home', allowed: true },
              { to: '/accounts', name: 'Accounts', allowed: scopes.accountsList },
              {
                to: '/trial-invitations',
                name: enUS['vital.trialInvitations.title'],
                allowed: scopes.accountProvisioning,
              },
              {
                to: '/provisioning-plan-review',
                name: 'Provisioning Plan Review',
                allowed: scopes.accountProvisioning,
              },
              { to: '/calculator', name: 'Pricing Calculator', allowed: settings.pricingCalculator },
              { to: '/high-usage', name: 'High usage accounts', allowed: settings.enableHighUsageChart },
            ]}
          />

          <Routes>
            <Route path="/" element={<Navigate to="/accounts" replace />} />

            <Route path="/accounts/*" element={scopes.accountsList ? <AccountsWrapper /> : <AccessDenied />} />

            <Route
              path="/trial-invitations"
              element={
                scopes.accountProvisioning ? (
                  <Suspense fallback={<AppLoading />}>
                    <TrialInvitations />
                  </Suspense>
                ) : (
                  <AccessDenied />
                )
              }
            />

            <Route
              path="/provisioning-plan-review"
              element={
                scopes.accountProvisioning ? (
                  <PlanReviewContainer settings={settings} scopes={scopes} />
                ) : (
                  <AccessDenied />
                )
              }
            />

            <Route
              path="/calculator"
              element={settings.pricingCalculator ? <PricingCalculatorContainer /> : <AccessDenied />}
            />

            <Route
              path="/high-usage"
              element={settings.enableHighUsageChart ? <HighAccountUsageContainer /> : <AccessDenied />}
            />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Page>
      </OmnibarProvider>
    </BrowserRouter>
  );

  const { isFetching, error } = settings;

  let content;
  if (!isUserReady || isFetching) {
    content = renderLoading();
  } else if (error) {
    content = renderError(error);
  } else {
    content = user ? renderAuthenticated() : renderUnauthenticated();
  }

  return (
    <HelmetProvider>
      <ThemeProvider>
        <MessagesProvider messages={enUS}>
          {content}
          <NotificationRegion />
        </MessagesProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

initDatadogRUM();
initNewRelicRUM();

export default Root;
